import React, { useState, useEffect } from 'react';
import styles from './passwordChange.module.css'; // Updated import

const PasswordAdmin = () => {
  const [formData, setFormData] = useState({
    email: '',
    userType: 'applicant',
    newPassword: '',
    confirmPassword: '',
    searchSecurityCode: '',
    changePasswordSecurityCode: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', isError: false });
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showSearchSecurityCode, setShowSearchSecurityCode] = useState(false);
  const [showChangePasswordSecurityCode, setShowChangePasswordSecurityCode] = useState(false);

  const validate = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (showSearchSecurityCode && !formData.searchSecurityCode) {
      newErrors.searchSecurityCode = 'Security code is required';
    }

    if (showChangePasswordSecurityCode && !formData.changePasswordSecurityCode) {
      newErrors.changePasswordSecurityCode = 'Security code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setErrors({ email: 'Email is required to search' });
      return;
    }

    if (!showSearchSecurityCode) {
      setShowSearchSecurityCode(true);
      return;
    }

    if (!formData.searchSecurityCode) {
      setErrors({ searchSecurityCode: 'Security code is required' });
      return;
    }

    setIsLoading(true);
    setMessage({ text: '', isError: false });

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/search_user.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          userType: formData.userType,
          securityCode: formData.searchSecurityCode
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Search failed');
      }

      if (result.status === "success") {
        setSearchResults(result.data);
        setShowSearchResults(true);
        setShowSearchSecurityCode(false);
      } else {
        throw new Error(result.message || 'No user found with that email');
      }
    } catch (error) {
      setMessage({
        text: error.message,
        isError: true
      });
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    if (!showChangePasswordSecurityCode) {
      setShowChangePasswordSecurityCode(true);
      return;
    }

    if (!formData.changePasswordSecurityCode) {
      setErrors({ changePasswordSecurityCode: 'Security code is required' });
      return;
    }

    setIsLoading(true);
    setMessage({ text: '', isError: false });

    try {
      const endpoint = formData.userType === 'applicant' 
        ? 'admin_change_applicant_password.php' 
        : 'admin_change_office_password.php';

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          newPassword: formData.newPassword,
          securityCode: formData.changePasswordSecurityCode
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Password change failed');
      }

      if (result.status === "success") {
        setMessage({
          text: 'Password successfully changed',
          isError: false
        });
        setFormData(prev => ({
          ...prev,
          newPassword: '',
          confirmPassword: '',
          changePasswordSecurityCode: ''
        }));
        setShowChangePasswordSecurityCode(false);
      } else {
        throw new Error(result.message || 'Password change failed');
      }
    } catch (error) {
      setMessage({
        text: error.message,
        isError: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Password Administration</h2>
      
      <div className={styles.form}>
        <div className={styles.formGroup}>
          <label>User Type:</label>
          <div className={styles.radioGroup}>
            <label>
              <input
                type="radio"
                name="userType"
                value="applicant"
                checked={formData.userType === 'applicant'}
                onChange={handleChange}
              />
              Applicant
            </label>
            <label>
              <input
                type="radio"
                name="userType"
                value="office"
                checked={formData.userType === 'office'}
                onChange={handleChange}
              />
              Office
            </label>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter user's email"
          />
          {errors.email && <span className={styles.errorMessage}>{errors.email}</span>}
        </div>

        {showSearchSecurityCode && (
          <div className={styles.formGroup}>
            <label htmlFor="searchSecurityCode">Security Code for Search:</label>
            <input
              type="password"
              id="searchSecurityCode"
              name="searchSecurityCode"
              value={formData.searchSecurityCode}
              onChange={handleChange}
              placeholder="Enter security code"
            />
            {errors.searchSecurityCode && <span className={styles.errorMessage}>{errors.searchSecurityCode}</span>}
          </div>
        )}

        <div className={styles.buttonGroup}>
          <button 
            onClick={handleSearch}
            disabled={isLoading}
            className={styles.searchButton}
          >
            {isLoading ? 'Searching...' : showSearchSecurityCode ? 'Verify and Search' : 'Search User'}
          </button>
        </div>

        {message.text && (
          <div className={`${styles.message} ${message.isError ? styles.error : styles.success}`}>
            {message.text}
          </div>
        )}

        {showSearchResults && searchResults.length > 0 && (
          <div className={styles.searchResults}>
            <h3>User Found:</h3>
            <div className={styles.userDetails}>
              {formData.userType === 'applicant' ? (
                <>
                  <p><strong>Name:</strong> {searchResults[0].first_name} {searchResults[0].last_name}</p>
                  <p><strong>District:</strong> {searchResults[0].district}</p>
                  <p><strong>Taluka:</strong> {searchResults[0].taluka}</p>
                </>
              ) : (
                <>
                  <p><strong>Office Name:</strong> {searchResults[0].office_name}</p>
                  <p><strong>Division:</strong> {searchResults[0].division}</p>
                  <p><strong>District:</strong> {searchResults[0].district}</p>
                </>
              )}
            </div>

            <div className={styles.passwordChangeForm}>
              <h3>Change Password</h3>
              <div className={styles.formGroup}>
                <label htmlFor="newPassword">New Password:</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                />
                {errors.newPassword && <span className={styles.errorMessage}>{errors.newPassword}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword">Confirm Password:</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                />
                {errors.confirmPassword && <span className={styles.errorMessage}>{errors.confirmPassword}</span>}
              </div>

              {showChangePasswordSecurityCode && (
                <div className={styles.formGroup}>
                  <label htmlFor="changePasswordSecurityCode">Security Code for Password Change:</label>
                  <input
                    type="password"
                    id="changePasswordSecurityCode"
                    name="changePasswordSecurityCode"
                    value={formData.changePasswordSecurityCode}
                    onChange={handleChange}
                    placeholder="Enter security code"
                  />
                  {errors.changePasswordSecurityCode && <span className={styles.errorMessage}>{errors.changePasswordSecurityCode}</span>}
                </div>
              )}

              <button 
                onClick={handlePasswordChange}
                disabled={isLoading}
                className={styles.changeButton}
              >
                {isLoading ? 'Processing...' : showChangePasswordSecurityCode ? 'Verify and Change Password' : 'Change Password'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordAdmin;